import { Badge, type CrudConfig } from '@startupquickstart/frontend';

export type ProcessFailureFrequency = 'rarely' | 'sometimes' | 'often' | 'always';
export type ProcessRunFrequency =
  | 'daily'
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'ad_hoc';

/** Shape of a Process record as returned by the API list endpoint. */
export interface Process {
  id: string;
  title: string;
  description?: string | null;
  whyItMatters?: string | null;
  successCriteria?: string | null;
  failureModes?: string | null;
  failureFrequency?: ProcessFailureFrequency | null;
  runFrequency?: ProcessRunFrequency | null;
  estimatedMinutes?: number | null;
  ownerId?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Included in list responses for lightweight display. */
  owner?: {
    id: string;
    firstName?: string | null;
    lastName?: string | null;
    email: string;
  } | null;
}

/** Full process shape returned by the single-record endpoint. */
export interface ProcessWithSteps extends Process {
  steps?: Array<{
    id: string;
    body: string;
    sortOrder: number;
    createdAt: string;
    updatedAt: string;
  }>;
}

const RUN_FREQUENCY_OPTIONS: { label: string; value: ProcessRunFrequency }[] = [
  { label: 'Daily', value: 'daily' },
  { label: 'Weekly', value: 'weekly' },
  { label: 'Monthly', value: 'monthly' },
  { label: 'Quarterly', value: 'quarterly' },
  { label: 'Ad-hoc', value: 'ad_hoc' },
];

const FAILURE_FREQUENCY_OPTIONS: { label: string; value: ProcessFailureFrequency }[] = [
  { label: 'Rarely', value: 'rarely' },
  { label: 'Sometimes', value: 'sometimes' },
  { label: 'Often', value: 'often' },
  { label: 'Always', value: 'always' },
];

/** Format an owner record into a readable display name. */
function formatOwnerName(owner: Process['owner']): string {
  if (!owner) return '—';
  const name = [owner.firstName, owner.lastName].filter(Boolean).join(' ').trim();
  return name || owner.email;
}

/** Process CRUD configuration for use with CrudListView and related components. */
export const processCrudConfig: CrudConfig<Process> = {
  entityName: 'Process',
  entityNamePlural: 'Processes',

  endpoints: {
    list: '/processes',
    single: (id: string) => `/processes/${id}`,
    create: '/processes',
    update: (id: string) => `/processes/${id}`,
    delete: (id: string) => `/processes/${id}`,
  },

  fields: [
    {
      key: 'title',
      label: 'Title',
      type: 'text',
      required: true,
      placeholder: 'Name this process',
      validation: { min: 2, max: 255 },
    },
    {
      key: 'description',
      label: 'What is the process?',
      type: 'textarea',
      required: false,
      placeholder: 'In one or two sentences, what is this process?',
    },
    {
      key: 'whyItMatters',
      label: 'Why this matters',
      type: 'textarea',
      required: false,
      placeholder: 'Why is this process important? What outcome does it produce?',
    },
    {
      key: 'successCriteria',
      label: 'What success looks like',
      type: 'textarea',
      required: false,
      placeholder: 'How do you know the process worked?',
    },
    {
      key: 'failureModes',
      label: 'What failure looks like',
      type: 'textarea',
      required: false,
      placeholder: 'What does it look like when this process goes wrong?',
    },
    {
      key: 'runFrequency',
      label: 'How often is this run?',
      type: 'select',
      required: false,
      options: RUN_FREQUENCY_OPTIONS,
      format: (value: ProcessRunFrequency | null | undefined) => {
        if (!value) return <span className="text-muted-foreground">—</span>;
        const label = RUN_FREQUENCY_OPTIONS.find((o) => o.value === value)?.label ?? value;
        return <Badge variant="secondary">{label}</Badge>;
      },
    },
    {
      key: 'failureFrequency',
      label: 'How often does this fail?',
      type: 'select',
      required: false,
      options: FAILURE_FREQUENCY_OPTIONS,
      format: (value: ProcessFailureFrequency | null | undefined) => {
        if (!value) return <span className="text-muted-foreground">—</span>;
        const label = FAILURE_FREQUENCY_OPTIONS.find((o) => o.value === value)?.label ?? value;
        const variantClass =
          value === 'always' || value === 'often'
            ? 'bg-red-100 text-red-800 border-red-200'
            : value === 'sometimes'
              ? 'bg-amber-100 text-amber-800 border-amber-200'
              : 'bg-green-100 text-green-800 border-green-200';
        return (
          <Badge variant="outline" className={variantClass}>
            {label}
          </Badge>
        );
      },
    },
    {
      key: 'estimatedMinutes',
      label: 'Estimated minutes per run',
      type: 'number',
      required: false,
      placeholder: 'e.g. 30',
      format: (value: number | null | undefined) => {
        if (value == null) return <span className="text-muted-foreground">—</span>;
        return <span>{value} min</span>;
      },
    },
    {
      key: 'owner',
      label: 'Owner',
      type: 'custom',
      required: false,
      format: (value: Process['owner']) => {
        if (!value) return <span className="text-muted-foreground">Unassigned</span>;
        return <span>{formatOwnerName(value)}</span>;
      },
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'createdAt',
      label: 'Created',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: [
      'title',
      'owner',
      'runFrequency',
      'failureFrequency',
      'estimatedMinutes',
      'updatedAt',
    ],
    searchableFields: ['title'],
    sortableFields: [
      'title',
      'runFrequency',
      'failureFrequency',
      'estimatedMinutes',
      'createdAt',
      'updatedAt',
    ],
    defaultSort: { field: 'updatedAt', direction: 'desc' },
    itemsPerPage: 20,
    filters: [
      {
        key: 'runFrequency',
        label: 'Run frequency',
        type: 'multiselect',
        options: RUN_FREQUENCY_OPTIONS,
      },
      {
        key: 'failureFrequency',
        label: 'Failure frequency',
        type: 'multiselect',
        options: FAILURE_FREQUENCY_OPTIONS,
      },
    ],
    actions: {
      create: { enabled: true },
      view: { enabled: true },
      edit: { enabled: true },
      delete: { enabled: true },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Overview',
        description: 'What this process is and why it matters',
        fields: ['title', 'description', 'whyItMatters'],
        defaultExpanded: true,
      },
      {
        title: 'Outcomes',
        description: 'How you know it worked or didn’t',
        fields: ['successCriteria', 'failureModes', 'failureFrequency'],
        defaultExpanded: true,
      },
      {
        title: 'Cadence',
        description: 'Owner, run frequency, and estimated effort',
        fields: ['owner', 'runFrequency', 'estimatedMinutes'],
        defaultExpanded: true,
      },
      {
        title: 'System',
        description: 'Timestamps',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: false,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'Overview',
        description: 'What this process is and why it matters',
        fields: ['title', 'description', 'whyItMatters'],
        columns: 1,
      },
      {
        title: 'Outcomes',
        description: 'What success and failure look like',
        fields: ['successCriteria', 'failureModes', 'failureFrequency'],
        columns: 1,
      },
      {
        title: 'Cadence',
        description: 'Owner, run frequency, and estimated effort',
        fields: ['runFrequency', 'estimatedMinutes'],
        columns: 2,
      },
    ],
    validation: {
      title: { required: true, min: 2, max: 255 },
    },
  },
};
